import React from 'react'
import { ApolloError } from '@apollo/client'
import { Membership, useFetchUserMembership } from '@/services/MembershipService/MembershipService'
import { Order, useOrdersAndSubscriptions } from '@/services/PaymentsService'
import type { Subscription } from '@/services/PaymentsService'
import { ReactFCC } from '@/types/react'

interface PaymentContextProps {
  children: React.ReactNode
}

interface PaymentContextType {
  loading: boolean
  error?: ApolloError
  payments: Array<Order>
  refetch: () => Promise<unknown>
  subscriptions: Array<Subscription>
  membership?: Membership
  startPolling: (interval: number) => void
  stopPolling: () => void
}

const PaymentContext = React.createContext<PaymentContextType>({
  loading: true,
  error: undefined,
  payments: [],
  refetch: () => Promise.resolve(),
  subscriptions: [],
  membership: undefined,
  startPolling: () => {},
  stopPolling: () => {},
})

export const PaymentsContextProvider: ReactFCC<PaymentContextProps> = ({ children }) => {
  const {
    data: subscriptionsData,
    error,
    loading: subscriptionsLoading,
    refetch,
    startPolling,
    stopPolling,
  } = useOrdersAndSubscriptions()
  const { data: membershipData, loading: membershipLoading } = useFetchUserMembership()

  const payments = subscriptionsData?.orders || []
  const subscriptions = subscriptionsData?.marketSubscriptions || []

  const value = {
    loading: subscriptionsLoading || membershipLoading,
    error,
    payments,
    subscriptions,
    membership: membershipData?.user?.membership ?? undefined,
    refetch,
    startPolling,
    stopPolling,
  }

  return <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
}

export function usePaymentsContext() {
  return React.useContext(PaymentContext)
}
